import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getProfile } from '../services/profileService';
import { getUnreadNotificationCount, markNotificationsAsRead } from '../services/mfuUserService';

const HeaderContainer = styled.div`
  background: white;
  padding: 12px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: opacity 0.2s ease;
  min-width: 0;
  flex-shrink: 1;

  &:hover {
    opacity: 0.8;
  }
`;

const Logo = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
  flex-shrink: 0;
`;

const HeaderTitle = styled.h1`
  font-size: 14px;
  font-weight: bold;
  color: #666;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
  white-space: nowrap;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const NotificationContainer = styled.div`
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const BellIcon = styled.div`
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z' fill='%23666666'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const NotificationBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  background-color: #ff4444 !important;
  color: #ffffff !important;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  font-size: 10px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Noto Sans Thai', sans-serif !important;
  min-width: 16px;
  padding: 0 2px;
`;

const ProfileContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const ProfileName = styled.span`
  font-size: 12px;
  font-weight: bold;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80px;
`;

const ProfileImage = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const MainHeader = () => {
  const navigate = useNavigate();
  const [notificationCount, setNotificationCount] = useState(0);
  const [userData, setUserData] = useState({
    name: '',
    profileImage: null
  });

  useEffect(() => {
    const loadHeaderData = async () => {
      try {
        // Load profile
        const profileResponse = await getProfile();
        let profile = profileResponse?.data || profileResponse?.profile;

        // Get image path and convert to full Firebase URL
        let imagePath = profile.image || profile.profile_image || '';
        let imageUrl = null;
        if (imagePath && imagePath !== '') {
          if (imagePath.startsWith('http')) {
            imageUrl = imagePath;
          } else {
            imageUrl = `https://firebasestorage.googleapis.com/v0/b/diet-z.appspot.com/o/${imagePath}`;
          }
        }

        setUserData({
          name: profile.name || profile.firstname || '',
          profileImage: imageUrl
        });

        // Load unread notification count for bell icon
        try {
          const unreadResponse = await getUnreadNotificationCount();
          console.log('🔔 Unread notification count response:', unreadResponse);
          const unreadCount = unreadResponse?.data?.unread_count || unreadResponse?.unread_count || 0;
          setNotificationCount(unreadCount);
        } catch (error) {
          console.error('Error loading unread notification count:', error);
          setNotificationCount(0);
        }
      } catch (error) {
        console.error('Error loading header data:', error);
      }
    };

    loadHeaderData();
  }, []);

  const handleLogoClick = () => {
    navigate('/dashboard');
  };

  const handleNotificationClick = async () => {
    try {
      // Mark all notifications as read when user clicks bell
      await markNotificationsAsRead();
      console.log('✅ Marked all notifications as read');

      // Reset notification count to 0
      setNotificationCount(0);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }

    // Navigate to notifications page
    navigate('/notifications');
  };

  const handleProfileClick = () => {
    navigate('/profile-edit');
  };

  return (
    <HeaderContainer>
      <HeaderLeft onClick={handleLogoClick}>
        <Logo src="/logo.png" alt="MFU Logo" />
        <HeaderTitle>MFU Wellness Center</HeaderTitle>
      </HeaderLeft>
      <HeaderRight>
        <NotificationContainer onClick={handleNotificationClick}>
          <BellIcon />
          {notificationCount > 0 && (
            <NotificationBadge>{notificationCount}</NotificationBadge>
          )}
        </NotificationContainer>
        <ProfileContainer onClick={handleProfileClick}>
          <ProfileName>{userData.name || 'ผู้ใช้'}</ProfileName>
          <ProfileImage src={userData.profileImage || '/person.png'} alt="Profile" />
        </ProfileContainer>
      </HeaderRight>
    </HeaderContainer>
  );
};

export default MainHeader;
